import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchTrustRequisitions,
  createTrustRequisition,
  authoriseTrustRequisition,
  downloadTrustRequisitionPdf,
} from '../api';

const STATUS_BADGE = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_AUTH: 'bg-yellow-100 text-yellow-800',
  AUTHORISED: 'bg-green-100 text-green-700',
  PAID: 'bg-blue-100 text-blue-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  DRAFT: 'Draft',
  PENDING_AUTH: 'Pending Auth',
  AUTHORISED: 'Authorised',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
};

const emptyLine = () => ({ description: '', payee: '', amount: '' });

export default function TrustRequisitionPanel({ authAxios, showFormMessage, caseId }) {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([emptyLine()]);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await fetchTrustRequisitions(authAxios, caseId);
      setRequisitions(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load trust requisitions.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const removeLine = (idx) => {
    setLines(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const validLines = lines.filter(l => l.description && l.payee && l.amount);
    if (validLines.length === 0) {
      showFormMessage?.('error', 'Add at least one valid line item.');
      return;
    }
    setSaving(true);
    try {
      const total = validLines.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0);
      const req = await createTrustRequisition(authAxios, {
        case: caseId,
        status: 'PENDING_AUTH',
        total_amount: total.toFixed(2),
        notes,
      });
      // If backend supports nested creation, lines would be sent inline;
      // otherwise we need a separate endpoint. For now we store lines in notes.
      // TODO: When TrustRequisitionLine endpoint is added, POST lines individually.
      showFormMessage?.('success', `Requisition ${req.requisition_number} created.`);
      setShowForm(false);
      setNotes('');
      setLines([emptyLine()]);
      load();
    } catch (err) {
      showFormMessage?.('error', err?.response?.data?.detail || 'Failed to create requisition.');
    } finally {
      setSaving(false);
    }
  };

  const handleAuthorise = async (id) => {
    try {
      const updated = await authoriseTrustRequisition(authAxios, id);
      showFormMessage?.('success', `Requisition ${updated.requisition_number} authorised.`);
      load();
    } catch (err) {
      showFormMessage?.('error', err?.response?.data?.detail || 'Authorisation failed.');
    }
  };

  const handleDownloadPdf = async (id, reqNumber) => {
    try {
      const resp = await downloadTrustRequisitionPdf(authAxios, id);
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TrustRequisition_${reqNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showFormMessage?.('error', 'Failed to download PDF.');
    }
  };

  const totalLines = lines.reduce((s, l) => s + parseFloat(l.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-2" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading requisitions...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Trust Requisitions</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ New Requisition'}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-gray-50 rounded-xl border p-5 space-y-4">
          <p className="text-sm font-medium text-gray-700">New Trust Requisition</p>

          {/* Line items */}
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
              <div className="col-span-5">Description</div>
              <div className="col-span-3">Payee</div>
              <div className="col-span-3">Amount (BWP)</div>
              <div className="col-span-1"></div>
            </div>
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2">
                <input
                  className="col-span-5 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="Description"
                  value={line.description}
                  onChange={e => updateLine(idx, 'description', e.target.value)}
                  required
                />
                <input
                  className="col-span-3 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="Payee"
                  value={line.payee}
                  onChange={e => updateLine(idx, 'payee', e.target.value)}
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="col-span-3 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="0.00"
                  value={line.amount}
                  onChange={e => updateLine(idx, 'amount', e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => removeLine(idx)}
                  className="col-span-1 text-red-400 hover:text-red-600 text-lg"
                  title="Remove line"
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setLines(prev => [...prev, emptyLine()])}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add line
            </button>
          </div>

          {/* Total */}
          <div className="text-right text-sm font-semibold text-gray-800">
            Total: BWP {totalLines.toFixed(2)}
          </div>

          {/* Notes */}
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            rows={2}
            placeholder="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setLines([emptyLine()]); setNotes(''); }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Requisition'}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {requisitions.length === 0 && !showForm && (
        <div className="text-center py-8 text-gray-400 text-sm">
          No trust requisitions for this matter yet.
        </div>
      )}

      <div className="space-y-3">
        {requisitions.map(req => (
          <div key={req.id} className="bg-white border rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm text-gray-900">{req.requisition_number}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[req.status] || 'bg-gray-100 text-gray-600'}`}>
                    {STATUS_LABELS[req.status] || req.status_display || req.status}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Prepared by {req.created_by_name || '—'} on {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                </p>
                {req.authorised_by_name && (
                  <p className="text-xs text-green-700 mt-0.5">
                    Authorised by {req.authorised_by_name} on {req.authorised_at ? new Date(req.authorised_at).toLocaleDateString() : '—'}
                  </p>
                )}
                {req.notes && (
                  <p className="text-xs text-gray-500 mt-1 truncate">{req.notes}</p>
                )}

                {/* Lines summary */}
                {req.lines && req.lines.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {req.lines.map(line => (
                      <div key={line.id} className="text-xs text-gray-600 flex justify-between">
                        <span className="truncate max-w-[60%]">{line.description} ({line.payee})</span>
                        <span className="font-medium">BWP {parseFloat(line.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className="text-lg font-bold text-gray-800">
                  BWP {parseFloat(req.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
                <div className="flex gap-1">
                  {(req.status === 'DRAFT' || req.status === 'PENDING_AUTH') && (
                    <button
                      onClick={() => handleAuthorise(req.id)}
                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
                    >
                      Authorise
                    </button>
                  )}
                  {req.status !== 'DRAFT' && (
                    <button
                      onClick={() => handleDownloadPdf(req.id, req.requisition_number)}
                      className="px-2.5 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 border"
                    >
                      PDF
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
